import React, { useState, useEffect } from 'react';
import { logoUrl } from '../data/mock';
import { useLanguage, languages } from '../context/LanguageContext';
import { ChevronDown, Globe } from 'lucide-react';

const Navbar = () => {
  const { language, changeLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('pocetna');
  const [logoOpacity, setLogoOpacity] = useState(1);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);

  const navigationLinks = [
    { id: 'pocetna', label: t.nav.home, href: '#pocetna' },
    { id: 'o-nama', label: t.nav.about, href: '#o-nama' },
    { id: 'usluge', label: t.nav.services, href: '#usluge' },
    { id: 'galerija', label: t.nav.gallery, href: '#galerija' },
    { id: 'tim', label: t.nav.team, href: '#tim' },
    { id: 'kontakt', label: t.nav.contact, href: '#kontakt' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 50);
      
      const fadeStart = 100;
      const fadeEnd = 300;
      if (scrollY <= fadeStart) {
        setLogoOpacity(1);
      } else if (scrollY >= fadeEnd) {
        setLogoOpacity(1);
      } else {
        const opacity = 1 - ((scrollY - fadeStart) / (fadeEnd - fadeStart)) * 0.3;
        setLogoOpacity(Math.max(0.7, opacity));
      }
      
      const sections = navigationLinks.map(link => link.id);
      for (const section of [...sections].reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [t]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isLangDropdownOpen && !e.target.closest('.lang-dropdown')) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isLangDropdownOpen]);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsLangDropdownOpen(false);
  };

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setIsLangDropdownOpen(false);
  };

  const currentLang = languages[language];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          isScrolled || isMobileMenuOpen
            ? 'bg-black/95 backdrop-blur-md shadow-lg py-3'
            : 'bg-transparent py-4 md:py-6'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a 
              href="#pocetna" 
              onClick={(e) => handleNavClick(e, '#pocetna')} 
              className="flex items-center transition-opacity duration-300 flex-shrink-0"
              style={{ opacity: logoOpacity }}
              data-testid="navbar-logo"
            >
              <img
                src={logoUrl}
                alt="Studio 149 - Muški Frizerski Salon Zemun"
                className="h-10 sm:h-12 md:h-[62px] w-auto animate-gentle-float"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              {navigationLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`relative font-medium text-sm uppercase tracking-widest transition-colors duration-300 ${
                    activeSection === link.id ? 'text-[#0067ff]' : 'text-white hover:text-[#0067ff]'
                  }`}
                  data-testid={`nav-link-${link.id}`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-[#0067ff] transition-all duration-300 ${
                      activeSection === link.id ? 'w-full' : 'w-0'
                    }`}
                  />
                </a>
              ))}
              
              {/* Desktop Language Selector */}
              <div className="relative lang-dropdown">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsLangDropdownOpen(!isLangDropdownOpen);
                  }}
                  className="flex items-center space-x-1.5 text-white hover:text-[#0067ff] transition-colors duration-300 px-2 py-1 rounded border border-gray-700 hover:border-[#0067ff]"
                  data-testid="desktop-lang-selector"
                >
                  <Globe className="w-4 h-4 animate-globe-glow" />
                  <span className="text-xs uppercase font-medium">{currentLang.shortName}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isLangDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-black/95 backdrop-blur-md border border-gray-700 rounded-lg shadow-xl overflow-hidden min-w-[140px]">
                    {Object.values(languages).map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full flex items-center space-x-2 px-4 py-2.5 text-left transition-colors duration-200 ${
                          language === lang.code 
                            ? 'bg-[#0067ff]/20 text-[#0067ff]' 
                            : 'text-white hover:bg-gray-800'
                        }`}
                        data-testid={`lang-option-${lang.code}`}
                      >
                        <img src={lang.flag} alt={lang.name} className="w-5 h-4 object-cover rounded-sm" />
                        <span className="text-sm">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile: Language + Menu Button */}
            <div className="md:hidden flex items-center space-x-2">
              {/* Mobile Language Selector */}
              <div className="relative lang-dropdown">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsLangDropdownOpen(!isLangDropdownOpen);
                  }}
                  className="flex items-center space-x-1 text-white px-2 py-1.5 rounded border border-gray-700"
                  data-testid="mobile-lang-selector"
                >
                  <Globe className="w-4 h-4 animate-globe-glow" />
                  <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isLangDropdownOpen && !isMobileMenuOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-black/95 backdrop-blur-md border border-gray-700 rounded-lg shadow-xl overflow-hidden min-w-[130px] z-50">
                    {Object.values(languages).map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`w-full flex items-center space-x-2 px-3 py-2.5 text-left transition-colors duration-200 ${
                          language === lang.code 
                            ? 'bg-[#0067ff]/20 text-[#0067ff]' 
                            : 'text-white hover:bg-gray-800'
                        }`}
                      >
                        <img src={lang.flag} alt={lang.name} className="w-5 h-4 object-cover rounded-sm" />
                        <span className="text-sm">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMobileMenu}
                className="flex flex-col items-center justify-center w-10 h-10 focus:outline-none flex-shrink-0"
                aria-label={isMobileMenuOpen ? 'Zatvori meni' : 'Otvori meni'}
                data-testid="mobile-menu-button"
              >
                <span
                  className={`block w-6 h-0.5 bg-white transition-all duration-300 ease-out ${
                    isMobileMenuOpen ? 'rotate-45 translate-y-[3px]' : '-translate-y-1.5'
                  }`}
                />
                <span
                  className={`block w-6 h-0.5 bg-white transition-all duration-300 ease-out ${
                    isMobileMenuOpen ? 'opacity-0 scale-0' : 'opacity-100'
                  }`}
                />
                <span
                  className={`block w-6 h-0.5 bg-white transition-all duration-300 ease-out ${
                    isMobileMenuOpen ? '-rotate-45 -translate-y-[3px]' : 'translate-y-1.5'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-500 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.98)' }}
        data-testid="mobile-menu-overlay"
      >
        <div className="flex flex-col items-center justify-center h-full pt-16 bg-black">
          {navigationLinks.map((link, index) => (
            <a
              key={link.id}
              href={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className={`text-2xl font-serif uppercase tracking-widest py-4 transition-all duration-300 ${
                activeSection === link.id ? 'text-[#0067ff]' : 'text-white hover:text-[#0067ff]'
              }`}
              style={{
                transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(30px)',
                opacity: isMobileMenuOpen ? 1 : 0,
                transition: `all 0.4s ease-out ${0.1 + index * 0.08}s`
              }}
              data-testid={`mobile-nav-link-${link.id}`}
            >
              {link.label}
            </a>
          ))}
          
          {/* Language Selector in Mobile Menu */}
          <div 
            className="mt-8 flex items-center space-x-4"
            style={{
              transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(30px)',
              opacity: isMobileMenuOpen ? 1 : 0,
              transition: 'all 0.4s ease-out 0.45s'
            }}
          >
            {Object.values(languages).map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg transition-all duration-200 ${
                  language === lang.code 
                    ? 'bg-[#0067ff] text-white' 
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <img src={lang.flag} alt={lang.name} className="w-5 h-4 object-cover rounded-sm" />
                <span className="text-sm font-medium">{lang.shortName}</span>
              </button>
            ))}
          </div>
          
          {/* Contact info in mobile menu */}
          <div 
            className="mt-8 text-center"
            style={{
              transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(30px)',
              opacity: isMobileMenuOpen ? 1 : 0,
              transition: 'all 0.4s ease-out 0.5s'
            }}
          >
            <a 
              href="tel:+381629729671" 
              className="text-[#0067ff] text-lg font-medium hover:text-white transition-colors"
              data-testid="mobile-phone-link"
            >
              062/972 96 71
            </a>
            <p className="text-gray-500 text-sm mt-2">Cara Dušana 153, Zemun</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
